/**
 * PatientLandingPage — v171.2.0
 * 
 * Redesigned landing page:
 * - Unified nav with Patients/Physicians cross-links
 * - Hero with sharper copy + business plan stat
 * - Collapsible 4D accordion (click to expand)
 * - Merged trust + privacy section (was 3 separate sections)
 * - Integrated CTA in footer (removed duplicate CTA section)
 * - Language selector toggles all content
 * 
 * Sections: Nav → Hero → Specialties → 4D Process → Trust+Privacy → Footer+CTA
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Shield, Globe, ChevronDown, Check, Lock,
  Mic, Camera, Stethoscope, CheckCircle, User
} from 'lucide-react';
import PolicyModal from '../components/PolicyModal';
import LanguageSelector from '../components/LanguageSelector';
import { useI18n } from '../i18n'
import { mdPanelUrl } from '../utils/claimUrl.js'

// Logos
import orellaLogoWhite from '../assets/logos/orella-wordmark-white-transparent.png';
import orellaLogoNavy from '../assets/logos/orella-wordmark-navy-transparent.png';

// App screenshot for hero
import appScreenshot from '../assets/app_screenshot_tall.png';

// Specialty icons
import earIcon from '../assets/icons/ear_logo.svg';
import noseIcon from '../assets/icons/nose_logo.svg';
import throatIcon from '../assets/icons/throat_logo.svg';
import eyeIcon from '../assets/icons/eye_logo.svg';
import skinIcon from '../assets/icons/skin_logo.svg';
import stomachIcon from '../assets/icons/stomach_logo.svg';
import urinaryIcon from '../assets/icons/urinary_logo.svg';
import muscleIcon from '../assets/icons/muscle_and_joints.svg';
import headIcon from '../assets/icons/head_and_neck_logo.svg';
import painIcon from '../assets/icons/pain_logo.svg';
import wellnessIcon from '../assets/icons/wellness_logo.svg';

// Brand Colors
const C = {
  navy: '#15354F',
  blue: '#46A8F9',
  lightBlue: '#A7D9FC',
  iceBlue: '#E9F8FF',
  green: '#66D170',
  white: '#FFFFFF',
};

// Specialty icons data
const SPECIALTY_ICONS = [
  { key: 'ear', icon: earIcon },
  { key: 'nose', icon: noseIcon },
  { key: 'throat', icon: throatIcon },
  { key: 'eye', icon: eyeIcon },
  { key: 'skin', icon: skinIcon },
  { key: 'stomach-gut', icon: stomachIcon },
  { key: 'urinary', icon: urinaryIcon },
  { key: 'muscle-joints', icon: muscleIcon },
  { key: 'head-neck', icon: headIcon },
  { key: 'pain', icon: painIcon },
  { key: 'wellness', icon: wellnessIcon },
];

// 4D Steps — structure/colors stay here, text from i18n
const FOUR_DS_STRUCTURE = [
  { step: 1, key: 'describe', borderColor: C.lightBlue, iconBg: C.lightBlue, checkColor: C.lightBlue, Icon: Mic, iconTextColor: C.navy },
  { step: 2, key: 'detect', borderColor: C.blue, iconBg: C.blue, checkColor: C.blue, Icon: Camera, iconTextColor: C.white },
  { step: 3, key: 'diagnose', borderColor: C.navy, iconBg: C.navy, checkColor: C.navy, Icon: User, iconTextColor: C.white },
  { step: 4, key: 'deliver', borderColor: C.green, iconBg: C.green, checkColor: C.green, Icon: CheckCircle, iconTextColor: C.white },
];

// Trust/security items
const TRUST_ICONS = [
  { key: '1', icon: Lock },
  { key: '2', icon: Shield },
  { key: '3', icon: Shield },
  { key: '4', icon: Globe },
  { key: '5', icon: Lock },
];

// ═══════════════════════════════════════════════════════════════════
// COLLAPSIBLE 4D CARD
// ═══════════════════════════════════════════════════════════════════
function FourDCard({ step, isOpen, onToggle }) {
  const IconComp = step.Icon;
  return (
    <button
      onClick={onToggle}
      className={`w-full text-left rounded-2xl overflow-hidden transition-all duration-300 ${
        isOpen ? 'shadow-md' : 'shadow-sm hover:shadow-md'
      }`}
      style={{
        border: isOpen ? `2px solid ${step.borderColor}` : '1px solid #E2E8F0',
        borderLeftWidth: isOpen ? 4 : 4,
        borderLeftColor: step.borderColor,
        background: 'white',
      }}
    >
      <div className="p-5">
        {/* Header — always visible */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: step.iconBg }}>
              <IconComp className="w-5 h-5" style={{ color: step.iconTextColor }} />
            </div>
            <div>
              <h3 className="text-base font-bold" style={{ color: C.navy }}>{step.name}</h3>
              {!isOpen && (
                <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>{step.highlight}</p>
              )}
            </div>
          </div>
          <ChevronDown
            className="w-5 h-5 flex-shrink-0 transition-transform duration-300"
            style={{
              color: isOpen ? step.borderColor : '#94A3B8',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            }}
          />
        </div>

        {/* Expanded content */}
        {isOpen && (
          <div className="mt-4 pl-14">
            <p className="text-sm mb-4 leading-relaxed" style={{ color: '#475569' }}>
              <span className="font-bold" style={{ color: C.navy }}>{step.highlight}</span>{' '}
              {step.desc}
            </p>
            <ul className="space-y-2.5">
              {step.items.map((item, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm" style={{ color: '#64748B' }}>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${step.checkColor}20` }}>
                    <Check className="w-3 h-3" strokeWidth={3} style={{ color: step.checkColor }} />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// COLLAPSIBLE TRUST ITEM
// ═══════════════════════════════════════════════════════════════════
function TrustItem({ item, isOpen, onToggle }) {
  const Icon = item.icon;
  return (
    <button
      onClick={onToggle}
      className="w-full text-left transition-all duration-200"
      style={{
        background: isOpen ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${isOpen ? 'rgba(167,217,252,0.3)' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: 12,
        padding: '12px 16px',
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
            style={{ background: isOpen ? 'rgba(70,168,249,0.15)' : 'rgba(255,255,255,0.06)' }}
          >
            <Icon className="w-4 h-4" style={{ color: isOpen ? C.blue : 'rgba(255,255,255,0.4)' }} />
          </div>
          <span className="text-sm font-semibold transition-colors" style={{ color: isOpen ? 'white' : 'rgba(255,255,255,0.75)' }}>
            {item.title}
          </span>
        </div>
        <ChevronDown
          className="w-4 h-4 flex-shrink-0 transition-transform duration-250"
          style={{
            color: isOpen ? C.blue : 'rgba(255,255,255,0.3)',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
          }}
        />
      </div>
      {isOpen && (
        <p className="mt-2 pl-11 text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
          {item.desc}
        </p>
      )}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function PatientLandingPage() {
  const { t, currentLanguage } = useI18n()
  const navigate = useNavigate();
  const [showPolicy, setShowPolicy] = useState(null);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [open4D, setOpen4D] = useState(null);
  const [openTrust, setOpenTrust] = useState(null);

  // Translated specialties
  const SPECIALTIES = useMemo(() => SPECIALTY_ICONS.map(s => ({
    name: t('specialties.' + s.key), icon: s.icon,
  })), [t])

  // Translated 4D steps
  const FOUR_DS = useMemo(() => FOUR_DS_STRUCTURE.map(s => ({
    ...s,
    name: t('landing.' + s.key + 'StepName'),
    highlight: t('landing.' + s.key + 'Highlight'),
    desc: t('landing.' + s.key + 'Desc'),
    items: [t('landing.' + s.key + 'Item1'), t('landing.' + s.key + 'Item2'), t('landing.' + s.key + 'Item3')],
  })), [t])

  // Translated trust items
  const TRUST_FEATURES = useMemo(() => TRUST_ICONS.map((ti, i) => ({
    icon: ti.icon,
    title: t('landing.trustTitle' + (i + 1)),
    desc: t('landing.trustDesc' + (i + 1)),
  })), [t])

  // Translated data principles
  const DATA_PRINCIPLES = useMemo(() => [
    t('landing.dataPrinciple1'),
    t('landing.dataPrinciple2'),
    t('landing.dataPrinciple3'),
    t('landing.dataPrinciple4'),
    t('landing.dataPrinciple5'),
  ], [t])

  const handleOpenApp = () => navigate('/splash');
  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="screen-min-h bg-white" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>

      {/* ═══ NAV — unified with cross-links ═══ */}
      <nav className="fixed top-0 left-0 right-0 z-50 shadow-sm" style={{ backgroundColor: C.navy }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src={orellaLogoWhite} alt="Orella" className="h-7" />
            </div>

            {/* Center tabs — Patients / Physicians */}
            <div className="hidden sm:flex items-center gap-0 absolute left-1/2 -translate-x-1/2">
              <span
                className="px-5 py-1.5 text-[13px] font-bold text-white cursor-default"
                style={{ borderBottom: '2px solid white' }}
              >
                {t('landing.tabPatients')}
              </span>
              <a
                href={mdPanelUrl()}
                className="px-5 py-1.5 text-[13px] font-semibold transition-colors hover:text-white/70"
                style={{ color: 'rgba(255,255,255,0.45)', borderBottom: '2px solid transparent' }}
              >
                {t('landing.tabPhysicians')}
              </a>
            </div>

            {/* Right: Language + CTA */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setLanguageOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors hover:bg-white/10"
                style={{ border: '1.5px solid rgba(167, 217, 252, 0.4)' }}
              >
                <Globe size={13} style={{ color: C.lightBlue }} />
                <span className="text-[12.5px] font-bold text-white">{currentLanguage.nativeName}</span>
              </button>
              <button
                onClick={handleOpenApp}
                className="px-5 py-2.5 text-white rounded-xl text-sm font-semibold transition-all hover:brightness-110 shadow-lg"
                style={{ backgroundColor: C.blue }}
              >
                {t('landing.openApp')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ═══ HERO — streamlined ═══ */}
      <section className="pt-16" style={{ backgroundColor: C.navy }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="pt-4">
              {/* Badge */}
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
                style={{ backgroundColor: 'rgba(70, 168, 249, 0.15)', color: C.lightBlue }}
              >
                <Shield className="w-4 h-4" />
                {t('landing.badgeText')}
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="text-white">{t('landing.walkIntoEvery')}</span>
                <br />
                <span className="text-white">{t('landing.doctorsVisit')}</span>
                <span style={{ color: C.blue }}>{t('landing.prepared')}</span>
              </h1>

              {/* Subtext — single punchy line */}
              <p className="text-lg mb-3 leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.75)' }}>
                {t('landing.heroSubtextShort')}
              </p>

              {/* Problem stat from business plan */}
              <p className="text-[13px] font-semibold mb-8" style={{ color: C.lightBlue, opacity: 0.85 }}>
                {t('landing.problemStat')}
              </p>

              {/* Stats Row */}
              <div className="flex items-center gap-6 sm:gap-10 mb-8 flex-wrap">
                <div className="text-center">
                  <p className="text-3xl sm:text-4xl font-bold" style={{ color: C.blue }}>11</p>
                  <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{t('landing.specialties')}</p>
                </div>
                <div className="hidden sm:block h-12 w-px" style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }} />
                <div className="text-center">
                  <p className="text-3xl sm:text-4xl font-bold" style={{ color: C.lightBlue }}>11</p>
                  <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{t('landing.languages')}</p>
                </div>
                <div className="hidden sm:block h-12 w-px" style={{ backgroundColor: 'rgba(255, 255, 255, 0.15)' }} />
                <div className="text-center">
                  <p className="text-3xl sm:text-4xl font-bold text-white">{t('landing.free')}</p>
                  <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{t('landing.toUse')}</p>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={handleOpenApp}
                  className="px-8 py-4 text-white rounded-2xl font-semibold transition-all hover:brightness-110 shadow-xl text-lg flex items-center gap-2 breathe-blue"
                  style={{ backgroundColor: C.blue }}
                >
                  {t('landing.createFreeAccount')}
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button
                  onClick={scrollToHowItWorks}
                  className="px-8 py-4 rounded-2xl font-semibold transition-all text-lg border-2 hover:bg-white/10"
                  style={{ borderColor: 'rgba(255, 255, 255, 0.25)', color: 'white' }}
                >
                  {t('landing.howItWorks')}
                </button>
              </div>
            </div>

            {/* Phone Mockup */}
            <div className="hidden lg:flex justify-center items-center lg:justify-end">
              <div className="relative" style={{ transform: 'perspective(1200px) rotateY(-5deg)' }}>
                <div
                  className="absolute -inset-6 rounded-[3.5rem] blur-[50px] opacity-20"
                  style={{ backgroundColor: C.blue }}
                />
                <div
                  className="relative w-[290px] rounded-[48px] overflow-hidden"
                  style={{
                    backgroundColor: '#111',
                    padding: '10px',
                    boxShadow: '0 50px 100px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.1), inset 0 1px 0 rgba(255,255,255,0.15)'
                  }}
                >
                  <div className="absolute -right-[3px] top-[110px] w-[3px] h-8 rounded-r bg-[#222]" />
                  <div className="absolute -left-[3px] top-[140px] w-[3px] h-14 rounded-l bg-[#222]" />
                  <div className="rounded-[38px] overflow-hidden relative" style={{ height: 540 }}>
                    <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[82px] h-[22px] bg-[#111] rounded-[12px] z-10" />
                    <img
                      src={appScreenshot}
                      alt="Orella App"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave transition */}
        <div className="relative h-24 sm:h-32">
          <svg className="absolute bottom-0 w-full h-full" viewBox="0 0 1440 120" preserveAspectRatio="none" fill={C.iceBlue}>
            <path d="M0,120 L0,60 Q360,0 720,60 T1440,60 L1440,120 Z" />
          </svg>
        </div>
      </section>

      {/* ═══ SPECIALTIES — compact ═══ */}
      <section className="py-10 sm:py-14" style={{ backgroundColor: C.iceBlue }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <p className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: C.blue }}>
              {t('landing.coveredSpecialties')}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: C.navy }}>
              {t('landing.specialtiesCount')}
            </h2>
            <p className="text-sm max-w-xl mx-auto" style={{ color: '#64748B' }}>
              {t('landing.specialtiesDesc')}
            </p>
          </div>

          <div className="flex justify-center items-center gap-3 sm:gap-4 flex-wrap">
            {SPECIALTIES.map((s, i) => (
              <div key={i} className="group relative">
                <div
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center transition-all hover:scale-110 cursor-pointer"
                  style={{ backgroundColor: C.white, border: '1.5px solid #E2E8F0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                >
                  <img src={s.icon} alt={s.name} className="w-7 h-7 sm:w-8 sm:h-8 object-contain" />
                </div>
                <div className="absolute -bottom-9 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  <span className="text-xs font-bold px-2.5 py-1 rounded-lg shadow-lg" style={{ backgroundColor: C.white, color: C.navy, border: '1px solid #E2E8F0' }}>
                    {s.name}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm font-medium inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: 'rgba(70, 168, 249, 0.1)', color: C.navy }}>
              <Stethoscope className="w-4 h-4" style={{ color: C.blue }} />
              {t('landing.worksWithAnyDoctor')}
            </p>
          </div>
        </div>
      </section>

      {/* ═══ 4D SECTION — collapsible accordion ═══ */}
      <section id="how-it-works" className="py-14 sm:py-18" style={{ backgroundColor: C.white }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: C.blue }}>
              {t('landing.fourDsProcess')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: C.navy }}>
              {t('landing.completeHealthcare')}
            </h2>
            <p className="text-base max-w-xl mx-auto" style={{ color: '#64748B' }}>
              {t('landing.fourDClickToLearn')}
            </p>
          </div>

          <div className="space-y-3">
            {FOUR_DS.map((step, i) => (
              <FourDCard
                key={step.step}
                step={step}
                isOpen={open4D === i}
                onToggle={() => setOpen4D(open4D === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TRUST + PRIVACY — merged (was Trust Strip + Benefits + Security) ═══ */}
      <section className="py-14 sm:py-18" style={{ backgroundColor: C.navy }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            {/* Left: collapsible trust items */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-5"
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: C.lightBlue }}>
                <Shield className="w-4 h-4" />
                {t('landing.yourDataYourControl')}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">
                {t('landing.builtForPrivacy')}
              </h2>
              <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {t('landing.trustClickToLearn')}
              </p>

              <div className="space-y-2">
                {TRUST_FEATURES.map((item, i) => (
                  <TrustItem
                    key={i}
                    item={item}
                    isOpen={openTrust === i}
                    onToggle={() => setOpenTrust(openTrust === i ? null : i)}
                  />
                ))}
              </div>
            </div>

            {/* Right: data principles card */}
            <div className="rounded-2xl p-6 w-full" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-xs tracking-widest uppercase font-semibold mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {t('landing.dataOwnershipPrinciples')}
              </p>
              <ul className="space-y-4">
                {DATA_PRINCIPLES.map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-[18px] h-[18px] flex-shrink-0" style={{ color: C.green }} />
                    <span className="text-sm text-white">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CTA — orella blue ═══ */}
      <section className="py-14 sm:py-16" style={{ backgroundColor: C.blue }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            {t('landing.readyForBetter')}
          </h2>
          <p className="text-base mb-8" style={{ color: 'rgba(255,255,255,0.85)' }}>
            {t('landing.takeControlShort')}
          </p>
          <button
            onClick={handleOpenApp}
            className="px-8 py-3.5 bg-white rounded-2xl font-bold hover:bg-slate-50 transition-all shadow-xl text-base flex items-center gap-2 mx-auto breathe-blue"
            style={{ color: C.navy }}
          >
            {t('landing.createFreeAccount')}
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-sm mt-5" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {t('landing.freeToUse')}
          </p>
        </div>
      </section>

      {/* ═══ FOOTER — navy ═══ */}
      <footer className="py-12 text-white" style={{ backgroundColor: C.navy }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Footer links */}
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={orellaLogoWhite} alt="Orella" className="h-6" />
              </div>
              <p className="text-sm mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>orella, Inc.</p>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>700 S. Flower Street, Suite 1000</p>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>{t('landing.losAngelesCa90017')}</p>
              <p className="text-sm mt-2" style={{ color: 'rgba(255,255,255,0.5)' }}>{t('landing.tel3236556222')}</p>
            </div>
            <div>
              <p className="font-semibold mb-4 text-white">{t('landing.quickLinks')}</p>
              <div className="space-y-2 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <p className="hover:text-white cursor-pointer transition-colors">{t('landing.home')}</p>
                <p className="hover:text-white cursor-pointer transition-colors">{t('landing.howItWorks')}</p>
                <p className="hover:text-white cursor-pointer transition-colors">{t('landing.getStarted')}</p>
                <p className="hover:text-white cursor-pointer transition-colors">{t('landing.support')}</p>
              </div>
            </div>
            <div>
              <p className="font-semibold mb-4 text-white">{t('landing.legal')}</p>
              <div className="space-y-2 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <p className="hover:text-white cursor-pointer transition-colors" onClick={() => setShowPolicy('privacy')}>{t('landing.privacyPolicy')}</p>
                <p className="hover:text-white cursor-pointer transition-colors" onClick={() => setShowPolicy('terms')}>{t('landing.termsOfService')}</p>
                <p className="hover:text-white cursor-pointer transition-colors">{t('landing.hipaaNotice')}</p>
              </div>
            </div>
          </div>
          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {t('landing.allRightsReserved')}
            </p>
            <p className="text-sm flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <Shield className="w-4 h-4" />
              {t('landing.dataEncryptedSecure')}
            </p>
          </div>
        </div>
      </footer>

      {/* Policy modals */}
      {showPolicy === 'terms' && <PolicyModal title={t('landing.termsConditions')} type="terms" onClose={() => setShowPolicy(null)} />}
      {showPolicy === 'privacy' && <PolicyModal title={t("landing.privacyPolicy")} type="privacy" onClose={() => setShowPolicy(null)} />}

      {/* Language selector modal */}
      <LanguageSelector isOpen={languageOpen} onClose={() => setLanguageOpen(false)} />
    </div>
  );
}
